import { v4 as uuidv4 } from "uuid";

export interface OCPPMessage {
  messageTypeId: number;
  messageId: string;
  action?: string;
  payload?: any;
}

export class OCPPClient {
  private ws: WebSocket | null = null;
  private isHardRejected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 1000; // Start with 1 second
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private heartbeatIntervalSeconds = 10; // Default 5 minutes
  private pendingMessages = new Map<string, string>();
  private messageQueue: string[] = [];
  private connectionState:
    | "connecting"
    | "connected"
    | "disconnected"
    | "error" = "disconnected";

  constructor(
    private stationId: string,
    private onMessage: (message: any) => void,
    private onConnectionChange: (connected: boolean) => void,
    private onError: (error: string) => void,
  ) {}

  sendStartTransaction(data: {
    connectorId: number;
    idTag: string;
    meterStart: number;
    timestamp: string;
  }) {
    return this.sendMessage("StartTransaction", data);
  }

  getStationId(): string {
    return this.stationId;
  }

  connect(): void {
    this.isHardRejected = false;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.connectionState = "connecting";
    const BASE_WS_URL =
      process.env.REACT_APP_WS_URL || "ws://localhost:8080/ccms";
    const wsUrl = `${BASE_WS_URL}/ocpp/${this.stationId}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log(
          `WebSocket Pipe Open for ${this.stationId}. Waiting for Boot Approval...`,
        );
        this.connectionState = "connecting"; // ✅ Keep state as connecting
        this.onConnectionChange(true);

        // 1. Send boot immediately
        this.sendBootNotification();

        // Send boot notification immediately after connection
        this.sendBootNotification();

        // Heartbeat will start after BootNotification response

        // Process queued messages
        this.processMessageQueue();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (Array.isArray(message) && message.length >= 3) {
            const [messageTypeId, messageId, actionOrResult] = message;

            if (messageTypeId === 3) {
              // 1. Get original action and delete from pending immediately
              const originalAction = this.pendingMessages.get(messageId);
              this.pendingMessages.delete(messageId);

              // 2. Specialized logic for BootNotification
              if (originalAction === "BootNotification") {
                const status = actionOrResult.status;

                if (status === "Accepted") {
                  console.log("✅ Station Accepted. Starting Heartbeat.");
                  this.heartbeatIntervalSeconds = actionOrResult.interval || 300;
                  this.isHardRejected = false;
                  this.startHeartbeat();
                  this.connectionState = "connected";
                } else {
                  console.error(`🛑 Station REJECTED: ${status}. Kill connection.`);
                  this.isHardRejected = true;
                  this.stopHeartbeat();
                  this.onError(`Connection Rejected: Invalid or Expired Price Book.`);
                  this.ws?.close(4003, "OCPP Boot Rejected");
                }

                // Pass to UI with specific name for the useEffect listener
                this.onMessage({
                  messageTypeId: 3,
                  messageId,
                  action: "BootNotificationResponse",
                  payload: actionOrResult,
                });
              } else {
                // 3. Handle all other responses
                this.onMessage({
                  messageTypeId: 3,
                  messageId,
                  action: originalAction ? originalAction + "Response" : "UnknownResponse",
                  payload: actionOrResult,
                });
              }
            } else if (messageTypeId === 2) {
              // Handle incoming CALL (Requests from Java Backend)
              const [,, action, payload] = message;
              this.onMessage({
                messageTypeId: 2,
                messageId,
                action: action,
                payload: payload,
              });
            }
          }
        } catch (error) {
          console.error("Error parsing message:", error);
        }
      };

      this.ws.onclose = (event) => {
        console.log(
          `WebSocket connection closed: ${event.code} - ${event.reason}`,
        );
        this.connectionState = "disconnected";
        this.onConnectionChange(false);
        this.stopHeartbeat();

        // Attempt to reconnect if not a clean close
        if (
          event.code !== 1000 &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          setTimeout(() => this.reconnect(), this.reconnectInterval);
        }
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        this.connectionState = "error";
        this.onError("WebSocket connection error");
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      this.onError("Failed to create WebSocket connection");
    }
  }

  disconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
    this.connectionState = "disconnected";
    this.onConnectionChange(false);
  }

  private reconnect(): void {
    if (this.isHardRejected) {
      console.warn(
        "🚫 Reconnect skipped: Station is in Rejected state (Expired PriceBook).",
      );
      return;
    }
    this.reconnectAttempts++;
    this.reconnectInterval = Math.min(this.reconnectInterval * 2, 30000); // Max 30 seconds

    console.log(
      `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`,
    );
    this.connect();
  }

  sendMessage(action: string, payload: any): string {
    const messageId = uuidv4();
    const message = [2, messageId, action, payload]; // CALL message

    // Track pending message
    this.pendingMessages.set(messageId, action);

    if (this.isConnected()) {
      this.ws!.send(JSON.stringify(message));
      console.log("Sent OCPP message:", { messageId, action, payload });
    } else {
      // Queue message for later
      this.messageQueue.push(JSON.stringify(message));
      console.log("Queued OCPP message (not connected):", {
        messageId,
        action,
        payload,
      });
    }

    return messageId;
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.isConnected()) {
      const message = this.messageQueue.shift();
      if (message) {
        this.ws!.send(message);
      }
    }
  }
  // Inside your ocppClient.ts class
  // Inside ocppClient.ts
  sendCallResult(messageId: string, payload: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Standard OCPP 2.0.1 Result format: [MessageType, MessageId, Payload]
      const response = [3, messageId, payload];
      this.ws.send(JSON.stringify(response));
      console.log("Sent CallResult to CCMS:", response);
    }
  }

  sendBootNotification(): string {
    return this.sendMessage("BootNotification", {
      reason: "PowerUp",
      chargingStation: {
        vendorName: "SimulatorCorp",
        model: "SimCharger-Pro",
        serialNumber: `SC-${this.stationId}`,
        firmwareVersion: "1.0.0",
        modem: {
          iccid: "12345678901234567890",
          imsi: "123456789012345",
        },
      },
    });
  }

  sendHeartbeat(): string {
    return this.sendMessage("Heartbeat", {});
  }

  sendStatusNotification(
    connectorId: number,
    status: string,
    timestamp?: string,
  ): string {
    return this.sendMessage("StatusNotification", {
      timestamp: timestamp || new Date().toISOString(),
      connectorStatus: status,
      evseId: 1,
      connectorId: connectorId,
    });
  }

  sendAuthorize(idToken: string, type: string = "ISO14443"): string {
    return this.sendMessage("Authorize", {
      idToken: {
        idToken: idToken,
        type: type,
      },
    });
  }

  sendTransactionEvent(
    eventType: "Started" | "Updated" | "Ended",
    transactionId: string,
    connectorId: number,
    triggerReason?: string,
    idToken?: string,
    meterValue?: any, // This is your finalMeterValue
  ): string {
    const payload: any = {
      eventType,
      timestamp: new Date().toISOString(),
      transactionInfo: {
        transactionId: transactionId,
        chargingState: eventType === "Ended" ? "Idle" : "Charging",
      },
      evse: { id: 1, connectorId: connectorId },
    };

    // 1. Add idToken for Started events
    if (eventType === "Started" && idToken) {
      payload.idToken = { idToken, type: "ISO14443" };
    }

    // 2. Add triggerReason for all events if available
    if (triggerReason) {
      payload.triggerReason = triggerReason;
    }

    // 3. CRITICAL FIX: Add meterValue for ANY eventType if it exists
    if (meterValue !== undefined && meterValue !== null) {
      payload.meterValue = [
        {
          timestamp: new Date().toISOString(),
          sampledValues: [
            {
              value: meterValue.toString(),
              measurand: "Energy.Active.Import.Register",
            },
          ],
        },
      ];
    }

    return this.sendMessage("TransactionEvent", payload);
  }
  sendMeterValues(evseId: number, meterValue: any): string {
    return this.sendMessage("MeterValues", {
      evseId: evseId,
      meterValue: meterValue,
    });
  }

  sendDataTransfer(vendorId: string, messageId?: string, data?: any): string {
    const payload: any = {
      vendorId: vendorId,
    };

    if (messageId) payload.messageId = messageId;
    if (data) payload.data = data;

    return this.sendMessage("DataTransfer", payload);
  }

  private startHeartbeat(): void {
    console.log(
      `startHeartbeat called with interval: ${this.heartbeatIntervalSeconds} seconds (${this.heartbeatIntervalSeconds * 1000}ms)`,
    );
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected()) {
        console.log(
          `Sending automatic heartbeat (interval: ${this.heartbeatIntervalSeconds}s)`,
        );
        this.sendHeartbeat();
      }
    }, this.heartbeatIntervalSeconds * 1000); // Convert seconds to milliseconds
    console.log(`Heartbeat interval started successfully`);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    return this.connectionState;
  }
}
